/** Exporta somente o resultado já autorizado; fórmulas não podem ser executadas ao abrir o CSV. */
export function linhasRelatorio(resultado: unknown): Record<string, unknown>[] {
  if (Array.isArray(resultado)) return resultado.filter((v): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v));
  if (resultado !== null && typeof resultado === 'object') {
    const objeto = resultado as Record<string, unknown>;
    if (Array.isArray(objeto.data)) return linhasRelatorio(objeto.data);
    return [objeto];
  }
  return [];
}

export function textoValor(valor: unknown): string {
  if (valor === null || valor === undefined) return '—';
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (typeof valor === 'object') return JSON.stringify(valor);
  if (typeof valor === 'string') return valor;
  if (typeof valor === 'number' || typeof valor === 'bigint') return valor.toString();
  return '—';
}

/** Formatação de leitura; o CSV conserva os valores originais para tratamento dos dados. */
export function valorApresentacao(valor: unknown, chave: string): string {
  const texto = textoValor(valor);
  const estados: Record<string, string> = { ACTIVE: 'Ativo', INACTIVE: 'Inativo', PRODUTO: 'Produto', INSUMO: 'Insumo', SERVICO: 'Serviço', RASCUNHO: 'Rascunho', LIBERADA: 'Liberada', EM_PRODUCAO: 'Em produção', EM_ANDAMENTO: 'Em andamento', CONCLUIDA: 'Concluída', CANCELADA: 'Cancelada', CRIADA: 'Criada' };
  if (estados[texto]) return estados[texto];
  const movimentos: Record<string, string> = { ENTRADA: 'Entrada', SAIDA: 'Saída', CONSUMO: 'Consumo', ESTORNO: 'Estorno', SINISTRO: 'Sinistro', TRANSFERENCIA: 'Transferência', ENTRADA_COMPRA: 'Entrada de compra', SALDO_ABERTURA: 'Saldo de abertura', AJUSTE_INVENTARIO: 'Ajuste de inventário', SAIDA_TINTA: 'Saída de tinta', DEVOLUCAO_TINTA: 'Devolução de tinta', PERDA: 'Perda', CONSUMIVEL_SETOR: 'Consumível do setor', ENCERRADA: 'Encerrada', ENCERRADA_PARCIAL: 'Encerrada parcialmente' };
  if (movimentos[texto]) return movimentos[texto];
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:/.test(valor)) {
    const iso = valor.replace(' ', 'T');
    const data = new Date(/(Z|[+-]\d{2}(:?\d{2})?)$/.test(iso) ? iso : iso + 'Z');
    if (!Number.isNaN(data.getTime())) return data.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  }
  if (!/id|codigo|numero/i.test(chave) && (typeof valor === 'number' || typeof valor === 'string' && /^-?\d+(\.\d+)?$/.test(valor))) return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 }).format(Number(valor));
  return texto;
}

/** Identificadores e índices não são grandezas: somá-los produz um total sem significado. */
export function colunaSomavel(chave: string, valor: unknown): boolean {
  if (/(^id$|Id$|_id$|codigo|numero|data|created|updated|percentual|percent|Pct$|taxa|indice|posicao|ordem|ranking|PorHora|PorPeca|padraoSegundos)/i.test(chave)) return false;
  return typeof valor === 'number' && Number.isFinite(valor) || typeof valor === 'string' && /^-?\d+(\.\d+)?$/.test(valor) && Number.isFinite(Number(valor));
}

export function rotuloColuna(chave: string): string {
  const materiais: Record<string, string> = { family: 'Família', unit: 'Unidade', weightG: 'Peso unitário (g)', areaUnitariaM2: 'Área unitária (m²)' };
  if (materiais[chave]) return materiais[chave];
  const conhecidos: Record<string, string> = { id: 'Identificador', codigo: 'Código', nome: 'Nome', kind: 'Tipo', status: 'Situação', categoria_codigo: 'Código da categoria', categoria_nome: 'Categoria', categoriaCodigo: 'Código da categoria', categoriaNome: 'Categoria', created_at: 'Criado em', updated_at: 'Atualizado em', createdAt: 'Criado em', updatedAt: 'Atualizado em' };
  if (conhecidos[chave]) return conhecidos[chave];
  const rotulos: Record<string, string> = { opNumero: 'OP', osStatus: 'Situação da etapa', opStatus: 'Situação da OP', clienteNome: 'Cliente', celulaNome: 'Célula', celulaCodigo: 'Código da célula', totalOps: 'Total de OPs', qtdTotalPecas: 'Quantidade de peças', qtdRefugoTotal: 'Quantidade de refugo', criadoEm: 'Criado em', geradoEm: 'Gerado em', etapaOrdem: 'Etapa', filaPosicaoManual: 'Posição na fila' };
  return rotulos[chave] ?? chave.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, v => v.toUpperCase());
}

export function criarCsv(resultado: unknown): string {
  const linhas = linhasRelatorio(resultado);
  const colunas = [...new Set(linhas.flatMap(linha => Object.keys(linha)))];
  const campo = (valor: unknown): string => {
    const texto = valor === null || valor === undefined ? '' : textoValor(valor);
    const seguro = /^[\s]*[=+@-]/.test(texto) ? `'${texto}` : texto;
    return `"${seguro.replace(/"/g, '""')}"`;
  };
  return '\uFEFF' + [colunas.map(rotuloColuna).map(campo).join(';'), ...linhas.map(linha => colunas.map(chave => campo(linha[chave])).join(';'))].join('\r\n');
}

export function baixarCsv(resultado: unknown, codigo: string): void {
  const url = URL.createObjectURL(new Blob([criarCsv(resultado)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `relatorio-${codigo.replace(/[^a-zA-Z0-9-]/g, '')}.csv`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function htmlImpressao(resultado: unknown, titulo: string): string {
  const escapar = (v: string): string => v.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
  const linhas = linhasRelatorio(resultado);
  const colunas = [...new Set(linhas.flatMap(linha => Object.keys(linha)))];
  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapar(titulo)}</title><style>body{font:12px system-ui;color:#1c3038;padding:24px}table{border-collapse:collapse;width:100%}th,td{padding:8px;border-bottom:1px solid #dfe7e3;text-align:left;overflow-wrap:anywhere}thead{display:table-header-group}h1{font-size:22px}@page{size:landscape;margin:12mm}</style></head><body><h1>${escapar(titulo)}</h1><p>Pieltec Gestão Industrial · ${linhas.length} registros</p>${linhas.length ? `<table><thead><tr>${colunas.map(c => `<th>${escapar(rotuloColuna(c))}</th>`).join('')}</tr></thead><tbody>${linhas.map(l => `<tr>${colunas.map(c => `<td>${escapar(valorApresentacao(l[c], c))}</td>`).join('')}</tr>`).join('')}</tbody></table>` : '<p>Nenhum registro para os filtros selecionados.</p>'}</body></html>`;
}

/** O navegador oferece impressão e Salvar como PDF, sem serviço externo ou nova dependência. */
export function imprimirRelatorio(resultado: unknown, titulo: string): boolean {
  const janela = window.open('', '_blank');
  if (!janela) return false;
  janela.opener = null;
  janela.document.write(htmlImpressao(resultado, titulo));
  janela.document.close();
  janela.focus();
  janela.print();
  return true;
}
